import { useCallback, useEffect, useState } from "react";
import { portal, type PortalPayable } from "../../../lib/portal";

/**
 * Which of these invoices can be paid by card right now, and the way to start.
 *
 * Asked of the server, one small read per invoice, and never guessed from the
 * status and the balance. A button that says "Pay by card" has to end in a
 * payment page; whether it can is a rule the API owns (which statuses, whether
 * the desk has cards switched on, whether Stripe is configured at all), and a
 * front page that guessed would open with a button that leads to a refusal.
 *
 * An invoice whose answer has not arrived, or never does, is simply not in the
 * map. The row then offers "Open invoice" instead, which is always true.
 */
export function usePayable(ids: readonly string[], enabled: boolean): {
  payable: Record<string, PortalPayable>;
  /** The invoice whose payment page is being opened, to hold its button. */
  busyId: string | null;
  failed: boolean;
  pay: (id: string) => void;
} {
  const [payable, setPayable] = useState<Record<string, PortalPayable>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  // A plain string, so the effect below re-runs when the SET of invoices
  // changes and not on every render that builds a new array.
  const key = ids.join(",");

  useEffect(() => {
    if (!enabled || !key) return;
    let live = true;
    for (const id of key.split(",")) {
      void portal
        .invoicePayable(id)
        .then((answer) => {
          if (live) setPayable((prev) => ({ ...prev, [id]: answer }));
        })
        // No answer means no card button for that row. The invoice itself is
        // still one click away, so nothing is lost but a shortcut.
        .catch(() => {});
    }
    return () => {
      live = false;
    };
  }, [enabled, key]);

  const pay = useCallback((id: string) => {
    setBusyId(id);
    setFailed(false);
    void portal
      .payInvoice(id)
      // Leaving the page rather than opening a tab: a card form in a popup is
      // the shape a browser is most likely to block, and this is the one moment
      // the client must not be interrupted.
      .then((res) => window.location.assign(res.url))
      .catch(() => {
        setFailed(true);
        setBusyId(null);
      });
  }, []);

  return { payable, busyId, failed, pay };
}
